'use client';

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from '@/compat/navigation';
import {
  Bell,
  CalendarDays,
  Check,
  CheckCircle2,
  Circle,
  MessageSquare,
  MoreVertical,
  Pencil,
  Signal,
  Tag,
  UserRound,
} from 'lucide-react';
import { API } from '@pilot/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface TaskActivity {
  id: string;
  type: string;
  content?: string;
  oldValue?: string;
  newValue?: string;
  field?: string;
  actor?: {
    fullName?: string;
    avatar?: string;
  };
  createdAt: string;
}

interface TaskNotification {
  id: string;
  notificationIds: string[];
  issue: {
    id: string;
    code: string;
    title: string;
    status: string;
  };
  link?: string;
  isRead: boolean;
  createdAt: string;
  activities: TaskActivity[];
}

const STATUS_LABELS: Record<string, string> = {
  backlog: 'Backlog',
  todo: 'Todo',
  in_progress: 'In progress',
  in_review: 'In review',
  done: 'Done',
  cancelled: 'Cancelled',
};

const PRIORITY_LABELS: Record<string, string> = {
  none: 'No priority',
  low: 'Low priority',
  medium: 'Medium priority',
  high: 'High priority',
  urgent: 'Urgent priority',
};

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = Math.max(0, now.getTime() - date.getTime());
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 30) return `${diffDays} days ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function getInitial(name?: string): string {
  return name?.trim().charAt(0).toUpperCase() || 'P';
}

function ActivityGlyph({ type }: { type: string }) {
  const className = 'h-3.5 w-3.5';

  switch (type) {
    case 'comment':
      return <MessageSquare className={className} />;
    case 'status_change':
      return <Circle className={className} />;
    case 'priority_change':
      return <Signal className={className} />;
    case 'assignee_change':
      return <UserRound className={className} />;
    case 'label_change':
      return <Tag className={className} />;
    case 'date_change':
      return <CalendarDays className={className} />;
    case 'created':
      return <CheckCircle2 className={className} />;
    default:
      return <Pencil className={className} />;
  }
}

function activityText(activity: TaskActivity): string {
  switch (activity.type) {
    case 'comment':
      return activity.content || 'Added a comment';
    case 'status_change':
      return `Status set to ${STATUS_LABELS[activity.newValue || ''] || activity.newValue || 'a new status'}`;
    case 'priority_change':
      return `Priority set to ${PRIORITY_LABELS[activity.newValue || ''] || activity.newValue || 'a new priority'}`;
    case 'assignee_change':
      return 'Changed the assignee';
    case 'label_change':
      return 'Changed the label';
    case 'date_change':
      return `Updated ${activity.field === 'startDate' ? 'the start date' : 'the due date'}`;
    case 'title_change':
      return 'Updated the task title';
    case 'description_change':
      return 'Updated the task description';
    case 'created':
      return 'Created this task';
    default:
      return 'Updated this task';
  }
}

function ActivityRow({ activity }: { activity: TaskActivity }) {
  const actorName = activity.actor?.fullName || 'Someone';
  const showAvatar = activity.type === 'comment';

  return (
    <div className="relative flex min-w-0 items-center gap-2.5 py-1.5 pl-7 text-xs">
      <div className="absolute -left-[11px] flex h-5 w-5 items-center justify-center rounded-full border border-border bg-popover text-muted-foreground">
        {showAvatar ? (
          <Avatar className="h-5 w-5 rounded-[5px]">
            <AvatarImage src={activity.actor?.avatar} />
            <AvatarFallback className="rounded-[5px] bg-blue-600 text-[10px] font-medium text-white">
              {getInitial(actorName)}
            </AvatarFallback>
          </Avatar>
        ) : (
          <ActivityGlyph type={activity.type} />
        )}
      </div>
      <p className="min-w-0 flex-1 truncate text-foreground/90">
        <span className="font-medium">{actorName}</span>
        <span className="px-1.5 text-muted-foreground">•</span>
        <span>{activityText(activity)}</span>
      </p>
      <time className="shrink-0 text-muted-foreground" dateTime={activity.createdAt}>
        {formatTimeAgo(activity.createdAt)}
      </time>
    </div>
  );
}

export function NotificationBell() {
  const router = useRouter();
  const [tasks, setTasks] = useState<TaskNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await API.notifications.getAll(12);
      setTasks(data.taskNotifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchNotifications();
    const interval = window.setInterval(fetchNotifications, 30000);
    return () => window.clearInterval(interval);
  }, [fetchNotifications]);

  const markTaskRead = useCallback(async (task: TaskNotification) => {
    if (task.isRead) return;

    setTasks(current => current.map(item =>
      item.id === task.id ? { ...item, isRead: true } : item
    ));
    setUnreadCount(current => Math.max(0, current - 1));

    try {
      const result = await API.notifications.markAsRead({ issueIds: [task.id] });
      setUnreadCount(result.unreadCount);
    } catch (error) {
      console.error('Failed to mark task notification as read:', error);
      void fetchNotifications();
    }
  }, [fetchNotifications]);

  const openTask = useCallback(async (task: TaskNotification) => {
    await markTaskRead(task);
    if (task.link) {
      setIsOpen(false);
      router.push(task.link);
    }
  }, [markTaskRead, router]);

  const handleMarkAllRead = async () => {
    const previousTasks = tasks;
    const previousUnreadCount = unreadCount;
    setTasks(current => current.map(task => ({ ...task, isRead: true })));
    setUnreadCount(0);

    try {
      await API.notifications.markAllAsRead();
    } catch (error) {
      console.error('Failed to mark all task notifications as read:', error);
      setTasks(previousTasks);
      setUnreadCount(previousUnreadCount);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label="Task notifications">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[460px] max-w-[calc(100vw-24px)] overflow-hidden p-0" align="end">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold">Task notifications</h3>
            <p className="text-xs text-muted-foreground">Mentions and recent task activity</p>
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => void handleMarkAllRead()}
            >
              <Check className="mr-1 h-3 w-3" />
              Mark all read
            </Button>
          )}
        </div>

        <div className="max-h-[min(620px,calc(100vh-96px))] overflow-y-auto">
          {isLoading && tasks.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Loading tasks…</div>
          ) : tasks.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              <Bell className="mx-auto mb-2 h-8 w-8 opacity-40" />
              No task notifications yet
            </div>
          ) : (
            tasks.map(task => (
              <div
                key={task.id}
                role="button"
                tabIndex={0}
                className={`border-b px-3 py-3 transition-colors last:border-0 hover:bg-muted/45 ${task.isRead ? '' : 'bg-primary/[0.035]'}`}
                onClick={() => void openTask(task)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    void openTask(task);
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border bg-background">
                    <Circle className="h-4 w-4 text-red-500" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{task.issue.code}</p>
                        <p className="truncate text-sm text-foreground/90">{task.issue.title}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-0.5">
                        <button
                          type="button"
                          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                          aria-label={task.isRead ? 'Task notification is read' : 'Mark task notification as read'}
                          onClick={event => {
                            event.stopPropagation();
                            void markTaskRead(task);
                          }}
                        >
                          {task.isRead ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                        </button>
                        <button
                          type="button"
                          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                          aria-label="More task actions"
                          onClick={event => event.stopPropagation()}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {task.activities.length > 0 && (
                      <div className="ml-2 mt-2 border-l border-border pl-0.5">
                        {task.activities.map(activity => (
                          <ActivityRow key={activity.id} activity={activity} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
